/**
 * ProgressBar - Quiz Progress Indicator Component
 * 
 * Shows the current question number and total progress through the quiz.
 * 
 * Features:
 * - Question counter (X von Y)
 * - Visual progress bar
 * - Step indicators (dots)
 * - Percentage display option
 */

import React from 'react';
import { useTheme } from '../../../context/ThemeContext';

/**
 * ProgressBar component
 * @param {number} currentIndex - Current question index (0-based)
 * @param {number} total - Total number of questions
 * @param {boolean} showPercentage - Whether to show percentage
 * @param {boolean} showSteps - Whether to show step dots
 * @param {string} variant - Display variant: 'bar' | 'steps' | 'minimal'
 * @param {string} size - Size variant: 'sm' | 'md' | 'lg'
 */
export function ProgressBar({
  currentIndex = 0,
  total = 1,
  showPercentage = false,
  showSteps = false,
  variant = 'bar',
  size = 'md',
}) {
  const { darkMode } = useTheme();

  // Calculate progress
  const progress = total > 0 ? ((currentIndex + 1) / total) * 100 : 0;
  const currentQuestion = currentIndex + 1;

  // Size variants
  const sizeClasses = {
    sm: {
      bar: 'h-1',
      text: 'text-xs',
      dot: 'w-2 h-2',
      dotActive: 'w-3 h-3',
    },
    md: {
      bar: 'h-2',
      text: 'text-sm',
      dot: 'w-2.5 h-2.5',
      dotActive: 'w-3.5 h-3.5',
    },
    lg: {
      bar: 'h-3',
      text: 'text-base',
      dot: 'w-3 h-3',
      dotActive: 'w-4 h-4',
    },
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  // Minimal variant - just text
  if (variant === 'minimal') {
    return (
      <div className={`flex items-center gap-2 ${sizes.text}`}>
        <span className={darkMode ? 'text-white' : 'text-gray-900'}>
          Frage <span className="font-bold">{currentQuestion}</span>
        </span>
        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
          von {total}
        </span>
        {showPercentage && (
          <span className={`ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            ({Math.round(progress)}%)
          </span>
        )}
      </div>
    );
  }

  // Steps variant - show dots for each question
  if (variant === 'steps' && total <= 15) {
    return (
      <div className="flex flex-col gap-2">
        {/* Text label */}
        <div className={`flex items-center justify-between ${sizes.text}`}>
          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
            Frage {currentQuestion} von {total}
          </span>
          {showPercentage && (
            <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
              {Math.round(progress)}%
            </span>
          )}
        </div>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-1.5 flex-wrap">
          {Array.from({ length: total }, (_, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;

            return (
              <div
                key={index}
                className={`
                  rounded-full transition-all duration-200
                  ${isCurrent ? sizes.dotActive : sizes.dot}
                  ${isCurrent
                    ? 'bg-indigo-500 ring-2 ring-indigo-300'
                    : isCompleted
                      ? 'bg-green-500'
                      : darkMode
                        ? 'bg-gray-600'
                        : 'bg-gray-300'
                  }
                `}
                title={`Frage ${index + 1}`}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // Default bar variant
  return (
    <div className="w-full">
      {/* Text label */}
      <div className={`flex items-center justify-between mb-1.5 ${sizes.text}`}>
        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
          Frage <span className="font-semibold">{currentQuestion}</span> von {total}
        </span>
        {showPercentage && (
          <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
            {Math.round(progress)}%
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div
        className={`
          ${sizes.bar}
          w-full rounded-full overflow-hidden
          ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}
        `}
      >
        <div
          className={`
            ${sizes.bar}
            bg-gradient-to-r from-indigo-500 to-purple-500
            rounded-full
            transition-all duration-500 ease-out
          `}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Compact question counter badge
 */
export function QuestionBadge({ current, total, className = '' }) {
  const { darkMode } = useTheme();

  return (
    <span
      className={`
        inline-flex items-center px-2.5 py-1 rounded-full
        ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}
        text-sm font-medium
        ${className}
      `}
    >
      {current}/{total}
    </span>
  );
}

/**
 * Circular progress indicator
 */
export function CircularProgress({
  currentIndex = 0,
  total = 1,
  size = 60,
  strokeWidth = 4,
}) {
  const { darkMode } = useTheme();
  
  const progress = total > 0 ? ((currentIndex + 1) / total) * 100 : 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="-rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={darkMode ? '#374151' : '#E5E7EB'}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="url(#progressGradient)"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-500 ease-out"
        />
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Center text */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {currentIndex + 1}
        </span>
      </div>
    </div>
  );
}

export default ProgressBar;
